/**
 * Date utility functions.
 *
 * Extracted from auth.js — no global state dependencies.
 *
 * Usage:
 *   import { sortByDate, normalizeDateString } from './helpers/date-utils.js';
 */

import moment from 'moment';
import { dateFormats } from './number-formats.js';

export function sortByDate(array, dateField) {
	if (!array) return null;
	return array
		.map((item) => ({ date: new Date(item[dateField]), item }))
		.sort((a, b) => b.date - a.date)
		.map(({ item }) => item);
}

export function dateTimeUTC(timestamp) {
	let date = new Date(timestamp * (timestamp < 20000000000 ? 1000 : 1));
	let dateUTC = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
	return dateUTC;
}

export function timeStampUTC(date, in_ms = false) {
	let timestamp = Date.parse(date);
	let thisDate = new Date(timestamp);
	let timeStampUTC = timestamp - thisDate.getTimezoneOffset() * 60000;
	if (!in_ms) timeStampUTC /= 1000;
	return timeStampUTC;
}

const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
function normalizeDateCore(value, { knownDatestamp = false, preferIsoFirst = false } = {}) {
	if (value === null || value === undefined || value === '') return '';

	// Date object
	if (value instanceof Date) return moment(value).format('YYYY-MM-DD');

	const n = Number(value);
	if (Number.isFinite(n)) {
		// Unix milliseconds: modern-style 13-digit timestamps, positive or negative
		if (Math.abs(n) >= 1000000000000) return moment.utc(n).format('YYYY-MM-DD');

		// Excel serial date: small-ish positive integers that are NOT blocked by knownDatestamp = true
		if (n > 0 && n < 100000 && !knownDatestamp) {
			const serial = Math.floor(n);
			const d = new Date(EXCEL_EPOCH + serial * 86400000);
			return d.toISOString().slice(0, 10);
		}

		// Unix seconds (our standar): usually epoch seconds, positive or negative
		return moment.unix(n).utc().format('YYYY-MM-DD');
	}

	if (typeof value === 'string') {
		let s = value.trim();

		if (preferIsoFirst) {
			const iso = moment(s, moment.ISO_8601, true);
			if (iso.isValid()) return iso.format('YYYY-MM-DD');
		}

		// Remove common human clutter
		s = s
			.replace(/(\d+)(st|nd|rd|th)/gi, '$1') // 1st → 1
			.replace(/\./g, '') // Apr. → Apr
			.replace(/,/g, ''); // Apr 1, 1997

		// PASS 1 — strict formats
		let m = moment(s, dateFormats, true);
		if (m.isValid()) return m.format('YYYY-MM-DD');

		// PASS 2 — controlled fallback for genuinely date-like strings.
		// Avoid moment(s), which falls back to native Date parsing.
		if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
			m = moment.parseZone(s, moment.ISO_8601, true);
			if (m.isValid()) return m.format('YYYY-MM-DD');
		}

		// OLD PASS 2 — relaxed fallback (only for date-like strings)
		// if (/[0-9]/.test(s) || /jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec/i.test(s)) {
		// 	m = moment(s);
		// 	if (m.isValid()) return m.format('YYYY-MM-DD');
		// }
	}

	return '';
}

export function normalizeDateString(value, knownDatestamp = false) {
	return normalizeDateCore(value, { knownDatestamp });
}

export function normalizeDobDateString(value) {
	return normalizeDateCore(value, { preferIsoFirst: true });
}

// Accepted range for entered and imported dates (YYYY-MM-DD, e.g. from normalizeDateString).
export const DATE_MIN = '1800-01-01';
export const DATE_MAX = '2199-12-31';
export function isPlausibleDate(ymd) {
	return /^\d{4}-\d{2}-\d{2}$/.test(ymd || '') && ymd >= DATE_MIN && ymd <= DATE_MAX;
}

// Convert many possible date strings to a Unix timestamp at UTC noon.
// Normalizes to UTC noon so the same calendar date does not drift by browser timezone.
export function dateStringToUnix(dateStr) {
	if (!dateStr) return '';

	const m = moment.utc(dateStr, dateFormats, true);
	if (!m.isValid()) return '';

	return m.hour(12).minute(0).second(0).millisecond(0).unix().toString();
}

// UTC day-boundary helpers — use these for any data-path range comparisons.
// Do NOT use moment(str).startOf/endOf('day') without .utc() in helper/admin code.
export function startOfDayUTC(dateStr) {
	if (!dateStr) return null;
	if (typeof dateStr === 'number') return null; // raw Unix seconds must go through normalizeDateString first
	const m = moment.utc(dateStr);
	if (!m.isValid()) return null;
	return m.startOf('day').unix();
}

export function endOfDayUTC(dateStr) {
	if (!dateStr) return null;
	if (typeof dateStr === 'number') return null; // raw Unix seconds must go through normalizeDateString first
	const m = moment.utc(dateStr);
	if (!m.isValid()) return null;
	return m.endOf('day').unix();
}

// Local date + time + IANA timezone -> UTC instant, for the Scheduled Events
// campaign end-date/time/timezone fields. Mirrors NewServerless's
// system/wf/scheduled-events/create.py _local_to_utc() exactly: a nonexistent
// local time (DST spring-forward gap) is rejected, an ambiguous local time
// (DST fall-back) resolves to the earlier of the two possible instants.
//
// Uses native Intl (this codebase deliberately has no moment-timezone
// dependency -- see report-viewer.js's 'datetime' case comment) rather than
// moment.tz, via the standard two-offset-candidate technique: JS has no
// equivalent of Python's datetime.fold, so gap/fold detection is done by
// computing the UTC instant under both offsets that could plausibly apply
// near a transition, then round-tripping each back to local time -- the same
// round-trip check the backend itself uses for gap detection.
function _offsetMinutesAt(utcMillis, timeZone) {
	const dtf = new Intl.DateTimeFormat('en-US', {
		timeZone,
		hourCycle: 'h23',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});
	const parts = dtf.formatToParts(new Date(utcMillis)).reduce((acc, p) => {
		if (p.type !== 'literal') acc[p.type] = p.value;
		return acc;
	}, {});
	const asIfUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
	return (asIfUTC - utcMillis) / 60000;
}

function _wallClockPartsAt(utcMillis, timeZone) {
	const dtf = new Intl.DateTimeFormat('en-US', {
		timeZone,
		hourCycle: 'h23',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
	const parts = dtf.formatToParts(new Date(utcMillis)).reduce((acc, p) => {
		if (p.type !== 'literal') acc[p.type] = p.value;
		return acc;
	}, {});
	return { year: +parts.year, month: +parts.month, day: +parts.day, hour: +parts.hour, minute: +parts.minute };
}

/**
 * Converts a local date + time + IANA timezone to a UTC instant.
 * Returns { utcMillis, error } -- utcMillis is null on error.
 *
 * @param {string} localDate - 'YYYY-MM-DD'
 * @param {string} localTime - 'HH:MM' (24-hour)
 * @param {string} timezone - IANA Area/Location name, e.g. 'America/New_York'
 */
export function localDateTimeToUtc(localDate, localTime, timezone) {
	if (!timezone || timezone.indexOf('/') === -1) {
		return { utcMillis: null, error: `'${timezone}' must be an IANA Area/Location timezone (e.g. 'America/New_York'), not an abbreviation` };
	}

	const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate || '');
	const timeMatch = /^(\d{2}):(\d{2})$/.exec(localTime || '');
	if (!dateMatch || !timeMatch) {
		return { utcMillis: null, error: `'${localDate} ${localTime}' is not a valid date/time (expected YYYY-MM-DD and HH:MM)` };
	}
	const [, y, mo, d] = dateMatch.map(Number);
	const [, h, mi] = timeMatch.map(Number);

	// Confirm the timezone name is real -- an invalid IANA name makes Intl throw.
	try {
		new Intl.DateTimeFormat('en-US', { timeZone: timezone });
	} catch (e) {
		return { utcMillis: null, error: `'${timezone}' is not a recognized IANA timezone` };
	}

	// Anchor: treat the requested wall-clock numbers as if they were UTC, then
	// find what offset `timezone` actually has near that instant.
	const naiveUTC = Date.UTC(y, mo - 1, d, h, mi, 0);
	const offset1 = _offsetMinutesAt(naiveUTC, timezone);
	const candidate1 = naiveUTC - offset1 * 60000;
	const offset2 = _offsetMinutesAt(candidate1, timezone);
	const candidate2 = naiveUTC - offset2 * 60000;

	const wants = { year: y, month: mo, day: d, hour: h, minute: mi };
	const matches = (utcMillis) => {
		const p = _wallClockPartsAt(utcMillis, timezone);
		return p.year === wants.year && p.month === wants.month && p.day === wants.day && p.hour === wants.hour && p.minute === wants.minute;
	};

	if (offset1 === offset2) {
		// No nearby transition -- unambiguous.
		return { utcMillis: candidate1, error: null };
	}

	const candidate1Matches = matches(candidate1);
	const candidate2Matches = matches(candidate2);

	if (!candidate1Matches && !candidate2Matches) {
		return { utcMillis: null, error: `'${localDate} ${localTime}' does not exist in ${timezone} (falls in a DST transition gap)` };
	}
	if (candidate1Matches && candidate2Matches) {
		// Ambiguous (DST fall-back) -- resolve to the earlier of the two instants,
		// matching the backend's fold=0 behavior.
		return { utcMillis: Math.min(candidate1, candidate2), error: null };
	}
	return { utcMillis: candidate1Matches ? candidate1 : candidate2, error: null };
}

/**
 * Detects the browser's local IANA timezone (via Intl), constrained to a
 * caller-supplied allowlist of values a particular <select> actually offers.
 * Returns '' (no preselection) if detection fails or the detected zone isn't
 * one of the offered options -- callers must never select a value the
 * dropdown doesn't actually have.
 *
 * @param {string[]} supportedTimezones - the exact option values available
 */
export function detectBrowserTimezone(supportedTimezones) {
	try {
		const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return supportedTimezones.includes(tz) ? tz : '';
	} catch (e) {
		return '';
	}
}

// Lightweight date formatter — avoids creating moment objects per row
// Uses UTC methods to prevent timezone shifts (timestamps are stored as UTC dates)
const _dateFormatCache = new Map();
export { isPastOrDateless } from './event-utils.js';

export function fastFormatDate(unixTimestamp) {
	if (_dateFormatCache.has(unixTimestamp)) return _dateFormatCache.get(unixTimestamp);
	const d = new Date(Number(unixTimestamp) * 1000);
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	const result = months[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
	_dateFormatCache.set(unixTimestamp, result);
	return result;
}
